import { useEffect, useMemo, useRef, useState } from "react"
import { Link, router, usePage } from "@inertiajs/react"
import {
  AudioLines,
  BookOpen,
  ChevronLeft,
  Image as ImageIcon,
  Info,
  MessageSquare,
  Plus,
  Users,
  Video,
} from "lucide-react"

import AppShell from "@/components/tuw/AppShell"
import { Avatar, AvatarStack } from "@/components/tuw/Avatar"
import SearchField from "@/components/tuw/SearchField"
import StatusBadge from "@/components/tuw/StatusBadge"
import MessageThread from "@/components/tuw/chat/MessageThread"
import ChatComposer from "@/components/tuw/chat/ChatComposer"
import { useShellActivity } from "@/components/tuw/ShellActivity"
import { Button } from "@/components/ui/button"
import useMediaQuery from "@/hooks/useMediaQuery"
import useOptimisticSend from "@/hooks/useOptimisticSend"
import ComposeModal, { composePick } from "@/components/tuw/messages/ComposeModal"
import InboxEmptyState from "@/components/tuw/messages/InboxEmptyState"
import { csrfToken } from "@/lib/orderActions"
import { launchVideoCall } from "@/lib/videoCall"
import { cn } from "@/lib/utils"

const TILE_ICON = { book: BookOpen, image: ImageIcon, audio: AudioLines }

// The service-icon tile on a project conversation row (book / image / audio).
function ConvTile({ tile, size = 42 }) {
  const Icon = TILE_ICON[tile] ?? BookOpen
  return (
    <div
      className="grid shrink-0 place-items-center rounded-[11px] border border-border bg-secondary"
      style={{ width: size, height: size }}
    >
      <Icon className="text-muted-foreground" style={{ width: size * 0.45, height: size * 0.45 }} strokeWidth={1.8} />
    </div>
  )
}

// A single conversation in the left list.
function ConvRow({ conv, active, onClick }) {
  const unread = conv.unread > 0
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "relative flex w-full gap-3 border-b border-border px-[18px] py-[13px] text-left transition-colors hover:bg-accent",
        active && "bg-accent before:absolute before:top-2.5 before:bottom-2.5 before:left-0 before:w-[3px] before:rounded-[3px] before:bg-primary"
      )}
    >
      {conv.type === "project" ? <ConvTile tile={conv.tile} /> : <Avatar initials={conv.initials} name={conv.name} size={42} />}

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className={cn("truncate font-display text-[13.5px] tracking-[-0.1px]", unread ? "font-bold" : "font-semibold")}>
            {conv.name}
          </span>
          <span className="shrink-0 text-[11px] font-medium text-ink-3">{conv.time}</span>
        </div>

        <div className="mt-[3px] flex items-center justify-between gap-2">
          <span className={cn("truncate text-[12.5px] leading-[1.4]", unread ? "text-muted-foreground" : "text-ink-3")}>
            {conv.preview?.who && <b className={cn("font-semibold", unread ? "text-foreground" : "text-muted-foreground")}>{conv.preview.who}: </b>}
            {conv.preview?.text}
          </span>
          {unread && (
            <span className="flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-primary px-[5px] text-[10.5px] font-bold text-primary-foreground">
              {conv.unread}
            </span>
          )}
        </div>

        <div className="mt-1 flex items-center gap-1.5 text-[11px] text-ink-3">
          {conv.type === "project" ? (
            <>
              <StatusBadge tier={conv.status.tier} label={conv.status.label} className="shrink-0 px-2 py-0.5 text-[10px]" />
              {/* Which order this is: a bundle's items share one title (UPDA-109). */}
              {conv.subtitle && <span className="truncate">{conv.subtitle}</span>}
            </>
          ) : (
            conv.role
          )}
        </div>
      </div>
    </button>
  )
}

// A "Teams" segment row. Team chat lives in the workspace, so this is a Link into
// /team at the right conversation — not a thread the inbox opens inline.
function TeamRow({ row }) {
  const unread = row.unread > 0
  return (
    <Link
      href={row.href}
      className="relative flex w-full gap-3 border-b border-border px-[18px] py-[13px] text-left transition-colors hover:bg-accent"
    >
      <div className="grid size-[42px] shrink-0 place-items-center rounded-[11px] border border-border bg-secondary">
        <Users className="size-[19px] text-muted-foreground" strokeWidth={1.8} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className={cn("truncate font-display text-[13.5px] tracking-[-0.1px]", unread ? "font-bold" : "font-semibold")}>
            {row.name}
          </span>
          {row.time && <span className="shrink-0 text-[11px] font-medium text-ink-3">{row.time}</span>}
        </div>
        <div className="mt-[3px] flex items-center justify-between gap-2">
          <span className={cn("truncate text-[12.5px] leading-[1.4]", unread ? "text-muted-foreground" : "text-ink-3")}>
            {row.preview?.who && <b className={cn("font-semibold", unread ? "text-foreground" : "text-muted-foreground")}>{row.preview.who}: </b>}
            {row.preview?.text ?? "No messages yet."}
          </span>
          {unread && (
            <span className="flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-primary px-[5px] text-[10.5px] font-bold text-primary-foreground">
              {row.unread}
            </span>
          )}
        </div>
        {row.teamName && <div className="mt-1 text-[11px] text-ink-3">{row.teamName}</div>}
      </div>
    </Link>
  )
}

function ThreadHeader({ conv, onBack }) {
  return (
    <div className="flex shrink-0 items-center gap-[13px] border-b border-border px-[22px] py-[15px]">
      {/* Phone-only back control (S-MSG-INBOX M4): the slide-over returns to the
          list without navigation; state lives on the list row / order page. */}
      <button
        type="button"
        aria-label="Back to messages"
        onClick={onBack}
        className="-ml-1.5 hidden size-9 shrink-0 items-center justify-center rounded-[10px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground max-[721px]:flex"
      >
        <ChevronLeft className="size-5" strokeWidth={2.2} />
      </button>
      {conv.type === "project" ? (
        <AvatarStack people={conv.team} size={38} />
      ) : (
        <Avatar initials={conv.initials} name={conv.name} size={40} />
      )}

      <div className="min-w-0 flex-1">
        <h3 className="flex items-center gap-[9px] font-display text-[15px] font-bold tracking-[-0.2px]">
          <span className="truncate">{conv.name}</span>
          {conv.type === "project" && (
            <StatusBadge tier={conv.status.tier} label={conv.status.label} className="px-2 py-0.5 text-[10px] max-[721px]:hidden" />
          )}
        </h3>
        <p className="mt-0.5 truncate text-[11.5px] text-ink-3">
          {conv.type === "project" ? conv.subtitle : conv.role || "Direct message"}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          title={conv.partnerUserId ? "Video call" : "Video call unavailable — no freelancer assigned"}
          disabled={!conv.partnerUserId}
          onClick={() =>
            launchVideoCall(conv.partnerUserId, {
              conversationSid: conv.sid,
              orderId: conv.orderId,
            })
          }
          className="grid size-[38px] place-items-center rounded-[10px] border border-border bg-card text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Video className="size-[17px]" strokeWidth={1.9} />
        </button>
        {conv.type === "project" && (
          <Link
            href={conv.orderHref}
            className="flex h-[38px] items-center gap-1.5 rounded-[10px] border border-border bg-card px-3.5 text-[12.5px] font-semibold text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground max-[721px]:hidden"
          >
            <Info className="size-[16px]" strokeWidth={1.9} />
            View project
          </Link>
        )}
      </div>
    </div>
  )
}

// Atrium Stage 5 — the unified Messages inbox. Left: a segmented, searchable
// list folding order team chats and freelancer DMs into one, plus a "Teams"
// segment linking into the Team workspace (team chat lives there; Messages is the
// entry point). Right: the thread, rendered with the SHARED chat components (same
// bubbles + composer as the order Chat tab, since they're the same Conversation
// surfaced twice). Outgoing bubbles use the calm `--bubble` blue. Sending posts to
// the nested /conversations/:sid/conversation_messages route (via useOptimisticSend),
// then the inbox reloads with the new message.
export default function Messages({ messages }) {
  const { auth } = usePage().props
  const { stamp } = useShellActivity()
  const canMessage = auth?.canMessage ?? true
  const conversations = messages.conversations ?? []
  const teams = messages.teams ?? []
  const counts = messages.counts ?? { all: 0, project: 0, person: 0 }

  // When arriving from a freelancer's "Message" button the server flags which
  // thread to open; otherwise fall back to the first unread (then the newest).
  const firstId = conversations.find((c) => c.unread > 0)?.id ?? conversations[0]?.id ?? null
  const [activeId, setActiveId] = useState(messages.openId ?? firstId)
  const [filter, setFilter] = useState("all")
  const [query, setQuery] = useState("")
  const [composing, setComposing] = useState(false)
  const composerRef = useRef(null)

  // Pocket Door (S-MSG-INBOX M3/M4): at phone width the list is home and the
  // thread pane is a slide-over. paneOpen tracks EXPLICIT intent only — a row
  // click, a compose pick, or a server deep link (openId — arriving from a
  // "Message" button is an explicit open, Temper TF-002). The auto first-unread
  // fallback above is never explicit.
  const isPhone = useMediaQuery("(max-width: 720px)")
  const [paneOpen, setPaneOpen] = useState(() => Boolean(messages.openId))

  // A server deep link can arrive while this page is already mounted: the
  // compose picker POSTs the profile's Message route, which redirects back here
  // with ?open=<id>, and Inertia keeps component state across a POST visit —
  // so the initial-state read above never runs again. Follow the prop
  // (UPDA-97).
  useEffect(() => {
    if (!messages.openId) return
    setActiveId(messages.openId)
    setPaneOpen(true)
  }, [messages.openId])
  const paneVisible = !isPhone || paneOpen

  const active = conversations.find((c) => c.id === activeId) ?? null

  // Persist a conversation's read state server-side (AQ-002): POST the session
  // read endpoint for its Conversation id, then reload only the inbox + topbar
  // unread props so the row badge, inbox counts and shell dot clear without a
  // full browser refresh. Idempotent — skipped when already read.
  // One write per conversation at a time. The read effect below now fires on
  // every unread change, not just on open, so a row click and the effect can
  // reach here in the same tick (AT-21).
  const markingRead = useRef(null)

  const markConversationRead = (conv) => {
    if (!conv || conv.unread <= 0 || conv.conversationId == null) return
    if (markingRead.current === conv.conversationId) return

    markingRead.current = conv.conversationId
    fetch(`/messages/conversations/${conv.conversationId}/read`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken() },
      credentials: "same-origin",
    })
      .then(() => router.reload({ only: ["messages", "unreadMessages"] }))
      .catch(() => {})
      .finally(() => {
        markingRead.current = null
      })
  }

  const openConversation = (conv) => {
    setActiveId(conv.id)
    setPaneOpen(true)
    markConversationRead(conv)
  }

  // Read-truth gate (Temper TF-001): pane VISIBILITY, not server-side
  // auto-selection, marks the active thread read. The page still auto-selects
  // the first unread thread into activeId (see firstId above), but at phone
  // width that pane sits off-canvas, so plain list-first entry must send no
  // read request. Desktop (paneVisible always true) keeps today's mount-read
  // for the auto-selected conversation; a deep link (openId → paneOpen true)
  // or a viewport resize that reveals the pane fires the existing read once.
  // Row clicks keep their direct markConversationRead call above.
  //
  // AT-21: this also re-fires whenever the OPEN thread's unread count moves off
  // zero, not only at open moments. The unread predicate counts any message past
  // the participant's read index including one the customer wrote, and the send
  // path never advances the sender's own index — so a reply, or a freelancer
  // message the live reload has already painted into the thread in front of
  // them, re-badged the row the customer was sitting in and stayed lit. The
  // order Chat tab already self-heals this way; this is the same shape.
  //
  // Still gated on paneVisible, so nothing is written while the pane is
  // off-canvas on phone (Temper TF-001), and still scoped to the active thread,
  // so the shell merely learning of a message never marks it read (AQ-003).
  useEffect(() => {
    if (!paneVisible) return
    markConversationRead(active)
  }, [paneVisible, active?.conversationId, active?.unread])

  // Live arrivals (AQ-026). The shell's shared refresher carries an activity
  // stamp that moves whenever a message lands in ANY of the customer's
  // conversations — including team channels, which never enter the dropdown's
  // thread slice. When it moves, re-derive the whole inbox from the presenter so
  // rows, previews, per-row unread counts and the Teams segment update in place,
  // with no navigation. The first observed value is the state this page already
  // rendered, so it is recorded, not acted on.
  //
  // Read-only: this path never marks anything read. The active thread's read
  // write stays on the open effects above.
  const seenStamp = useRef(null)
  useEffect(() => {
    if (stamp == null) return
    if (seenStamp.current === null || seenStamp.current === stamp) {
      seenStamp.current = stamp
      return
    }
    seenStamp.current = stamp
    router.reload({ only: ["messages"] })
  }, [stamp])

  // Optimistic send: sent messages render immediately from the pending queue,
  // reconcile with the server thread on reload, and surface a per-bubble error
  // with retry on failure. `serverItems` is the active thread; flagged "Edit"
  // pulls the rejected text back into the composer.
  const serverItems = active?.thread ?? []
  const { pendingItems, send, retry, editAndRemove, dismiss, sending } = useOptimisticSend({
    sid: active?.sid,
    only: ["messages"],
    serverItems,
  })

  const handleEdit = (clientId) => {
    composerRef.current?.refill(editAndRemove(clientId))
  }

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return conversations.filter((c) => {
      if (filter !== "all" && (filter === "project" ? c.type !== "project" : c.type !== "person")) return false
      if (!q) return true
      return `${c.name} ${c.role ?? ""} ${c.subtitle ?? ""} ${c.preview?.text ?? ""}`.toLowerCase().includes(q)
    })
  }, [conversations, filter, query])

  const groups = [
    { key: "today", label: "Today", rows: visible.filter((c) => c.group === "today") },
    { key: "earlier", label: "Earlier", rows: visible.filter((c) => c.group !== "today") },
  ]

  // The Teams segment only shows under the "All" filter (teams aren't projects or
  // people); it respects the search query the same way.
  const visibleTeams = useMemo(() => {
    if (filter !== "all") return []
    const q = query.trim().toLowerCase()
    if (!q) return teams
    return teams.filter((t) => `${t.name} ${t.teamName ?? ""} ${t.preview?.text ?? ""}`.toLowerCase().includes(q))
  }, [teams, filter, query])

  const people = messages.people ?? []
  const composeModal = composing && (
    <ComposeModal
      people={people}
      canMessage={canMessage}
      onClose={() => setComposing(false)}
      onPick={(p) => {
        composePick(p, {
          canMessage,
          select: (id) => {
            setActiveId(id)
            setPaneOpen(true)
          },
        })
        setComposing(false)
      }}
    />
  )

  // Whole-inbox empty state (no order/DM conversations AND no team chats).
  if (conversations.length === 0 && teams.length === 0) {
    return (
      <>
        <InboxEmptyState people={people} onCompose={() => setComposing(true)} />
        {composeModal}
      </>
    )
  }

  return (
    <>
      {/* Shell-aware inbox height: dvh tracks dynamic browser chrome in the
          ≤860 bands (design .inbox rule); desktop keeps the live 100vh. At
          phone width the container clips the thread slide-over. */}
      <div className="flex h-[calc(100vh-69px)] min-h-[560px] max-[861px]:h-[calc(100dvh-69px)] max-[721px]:relative max-[721px]:overflow-hidden">
        {/* ---- list pane ---- */}
        <div className="flex w-[366px] shrink-0 flex-col border-r border-border bg-card max-[861px]:w-[316px] max-[721px]:w-full max-[721px]:border-r-0">
          <div className="px-[18px] pt-[18px]">
            <div className="mb-3.5 flex items-center justify-between">
              <h1 className="font-display text-[21px] font-bold tracking-[-0.4px]">Messages</h1>
              <button
                type="button"
                title="New message"
                onClick={() => setComposing(true)}
                className="grid size-9 place-items-center rounded-[10px] border border-line-2 bg-card text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <Plus className="size-[18px]" strokeWidth={2} />
              </button>
            </div>

            <SearchField
              className="mb-[13px] bg-accent"
              inputClassName="text-[13px] md:text-[13px]"
              value={query}
              onChange={setQuery}
              placeholder="Search messages…"
            />

            <div className="flex gap-[3px] rounded-[10px] border border-border bg-secondary p-[3px]">
              {[
                { key: "all", label: "All", n: counts.all },
                { key: "project", label: "Projects", n: counts.project },
                { key: "person", label: "People", n: counts.person },
              ].map((seg) => {
                const on = filter === seg.key
                return (
                  <button
                    key={seg.key}
                    type="button"
                    onClick={() => setFilter(seg.key)}
                    className={cn(
                      "flex flex-1 items-center justify-center gap-1.5 rounded-[7px] px-2 py-[7px] text-[12.5px] font-semibold transition-colors",
                      on ? "bg-card text-foreground shadow-[0_1px_3px_rgba(0,0,0,0.06)]" : "text-muted-foreground"
                    )}
                  >
                    {seg.label}
                    <span className={cn("rounded-full px-1.5 text-[10.5px] font-bold leading-4", on ? "bg-[var(--red-soft)] text-primary" : "border border-border bg-card text-ink-3")}>
                      {seg.n}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          <div className="mt-1.5 flex-1 overflow-y-auto">
            {visible.length === 0 && visibleTeams.length === 0 ? (
              <div className="px-6 py-12 text-center text-[12.5px] text-ink-3">No conversations match.</div>
            ) : (
              <>
                {groups.map(
                  (group) =>
                    group.rows.length > 0 && (
                      <div key={group.key}>
                        <div className="px-[18px] pt-4 pb-[7px] text-[10px] font-bold tracking-[0.1em] text-ink-3 uppercase">
                          {group.label}
                        </div>
                        {group.rows.map((conv) => (
                          <ConvRow key={conv.id} conv={conv} active={conv.id === activeId} onClick={() => openConversation(conv)} />
                        ))}
                      </div>
                    )
                )}
                {visibleTeams.length > 0 && (
                  <div>
                    <div className="px-[18px] pt-4 pb-[7px] text-[10px] font-bold tracking-[0.1em] text-ink-3 uppercase">
                      Teams
                    </div>
                    {visibleTeams.map((row) => (
                      <TeamRow key={row.id} row={row} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ---- thread pane ---- */}
        {/* Phone slide-over (S-MSG-INBOX M4): a translate overlay above the
            list, reduced-motion safe. Desktop widths carry no transform. */}
        <div
          className={cn(
            "flex min-w-0 flex-1 flex-col bg-card",
            "max-[721px]:absolute max-[721px]:inset-0 max-[721px]:z-[5]",
            "transition-transform duration-[280ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none",
            paneOpen ? "max-[721px]:translate-x-0" : "max-[721px]:translate-x-full"
          )}
        >
          {active ? (
            <>
              <ThreadHeader conv={active} onBack={() => setPaneOpen(false)} />
              <MessageThread
                // A fresh thread per opening: on a phone, backing out only
                // slides the pane away, and reopening must land on the newest.
                key={`${active.id}:${paneOpen}`}
                items={[...serverItems, ...pendingItems]}
                onRetry={retry}
                onEdit={handleEdit}
                onDismiss={dismiss}
                empty={
                  <div className="m-auto max-w-[280px] text-center">
                    <MessageSquare className="mx-auto size-7 text-ink-3" strokeWidth={1.6} />
                    <p className="mt-3 text-[12.5px] leading-[1.55] text-muted-foreground">No messages yet. Say hello.</p>
                  </div>
                }
              />
              {active.type === "person" && !canMessage ? (
                <div className="shrink-0 border-t border-border bg-secondary px-5 py-3.5 text-center text-[12.5px] leading-[1.5] text-ink-3">
                  Direct messaging isn't available on your account right now. Reach out to support if you think this is a mistake.
                </div>
              ) : (
                <ChatComposer
                  ref={composerRef}
                  placeholder={active.type === "project" ? "Message your team…" : `Message ${active.name.split(" ")[0]}…`}
                  onSend={send}
                  sending={sending}
                  withAttach
                />
              )}
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center bg-accent px-10 text-center">
              <div className="mb-[18px] grid size-[58px] place-items-center rounded-2xl border border-border bg-card">
                <MessageSquare className="size-[26px] text-ink-3" strokeWidth={1.6} />
              </div>
              <h3 className="font-display text-base font-bold tracking-[-0.2px]">Select a conversation</h3>
              <p className="mt-1.5 max-w-[340px] text-[13px] leading-[1.55] text-ink-3">
                Choose a project or a person on the left to read and reply. Project chats reach your whole team; direct
                messages go to one freelancer.
              </p>
            </div>
          )}
        </div>
      </div>

      {composeModal}
    </>
  )
}

Messages.layout = (page) => (
  <AppShell active="Messages" crumb={["Messages"]}>
    {page}
  </AppShell>
)
